// The create-a-room WIZARD model (Set 12.5), lifted out of the DOM so it's testable without a browser.
// It's pure: given what the server offers (from /api/rooms — optional plugins with their config fields
// and optional-dependencies, and always-on core plugins that still expose options) plus the player's
// picks, it works out which plugins are actually enabled (dependency-closed), which plugins have
// options to configure, and the { plugin: { field: value } } config to POST. The DOM half (ui.js) just
// renders these and reads inputs back; all the rules live here.

// Canned plugin selections for the Plugins step's PRESET buttons (Set 12.7) — a lightweight precursor
// to the "bases" idea in the plugin-composition artifact. Each names a set of OPTIONAL plugin ids
// (dependencies close at apply time); "ALL" means every optional the server offers, [] clears.
export const PRESETS = [
    { id: "survival", label: "Survival", emoji: "🏕️", picks: ["hunger", "rations", "night-spawn", "daynight"] },
    { id: "homestead", label: "Homestead", emoji: "🌾", picks: ["agriculture", "cooking"] },
    { id: "everything", label: "Everything", emoji: "🧪", picks: "ALL" },
    { id: "none", label: "None", emoji: "⬛", picks: [] },
];

// The picks a preset resolves to against what the server actually offers (unknown ids dropped).
export function presetPicks(preset, offered) {
    if (preset.picks === "ALL") return new Set(offered.map((o) => o.id));
    const have = new Set(offered.map((o) => o.id));
    return new Set(preset.picks.filter((id) => have.has(id)));
}

// Order the plugin list for display: filter by a search term over id + label, and optionally sink
// ENABLED plugins to the end (Eric: "sort enabled to the end so you can see what you did"). Stable, so
// catalog order holds within each group. Pure — the DOM just renders what this returns.
export function orderPlugins(offered, enabledSet, { search = "", enabledLast = false } = {}) {
    const q = (search || "").trim().toLowerCase();
    let list = offered.filter((o) => !q || o.id.toLowerCase().includes(q) || (o.label || "").toLowerCase().includes(q));
    if (enabledLast) list = list.map((o, i) => [o, i]).sort((a, b) =>
        ((enabledSet.has(a[0].id) ? 1 : 0) - (enabledSet.has(b[0].id) ? 1 : 0)) || (a[1] - b[1])).map((p) => p[0]);
    return list;
}

// The optionals actually enabled by a set of picks, dependency-closed: picking Cooking pulls in
// Agriculture, which pulls in Hunger. `offered` is the OPTIONAL_SYSTEMS list (each { id, needs }).
export function enabledOptionals(offered, picked) {
    const byId = new Map(offered.map((o) => [o.id, o]));
    const on = new Set();
    const add = (id) => {
        if (on.has(id) || !byId.has(id)) return;
        on.add(id);
        for (const d of byId.get(id).needs || []) add(d);
    };
    for (const id of picked) add(id);
    return on;
}

// Which enabled optionals were pulled in by a DEPENDENCY rather than picked directly, mapped to the
// picked plugin(s) that required them — for the "also enables Hunger (needed by Cooking)" hint.
export function autoEnabled(offered, picked) {
    const pickedSet = picked instanceof Set ? picked : new Set(picked);
    const enabled = enabledOptionals(offered, pickedSet);
    const label = new Map(offered.map((o) => [o.id, o.label || o.id]));
    const by = {};
    // Any ENABLED plugin (picked or itself pulled in) attributes its deps, so a transitive chain shows
    // — Cooking -> Agriculture -> Hunger lists both Agriculture (by Cooking) and Hunger (by Agriculture).
    for (const o of offered) {
        if (!enabled.has(o.id)) continue;
        for (const d of o.needs || []) {
            if (enabled.has(d) && !pickedSet.has(d)) (by[d] || (by[d] = [])).push(label.get(o.id));
        }
    }
    return by; // { depId: [labels of the enabled plugins that require it] }
}

// The plugins that have options to configure, in the order the wizard shows them: always-on core
// plugins first (jetpack, …), then the enabled optionals that declare fields. `core` is CORE_CONFIG.
export function configurablePlugins(offered, core, enabledSet) {
    const list = [];
    for (const c of core || []) if (c.config && c.config.length) list.push({ id: c.id, label: c.label || c.id, always: true, fields: c.config });
    for (const o of offered || []) if (enabledSet.has(o.id) && o.config && o.config.length) list.push({ id: o.id, label: o.label || o.id, always: false, fields: o.config });
    return list;
}

// A field's default, normalised: a "multi" default is always a list, everything else a scalar.
export function fieldDefault(field) {
    if (field.type === "multi") return Array.isArray(field.default) ? field.default.slice() : (field.default == null ? [] : [field.default]);
    return field.default;
}

// True when a field's current value equals its default (so we can omit it from the POST — the server
// resolves unset fields to the plugin's declared default). Arrays compare order-insensitively.
export function isDefault(field, value) {
    const d = fieldDefault(field);
    if (field.type === "multi") {
        const a = (Array.isArray(value) ? value : []).slice().sort();
        const b = (Array.isArray(d) ? d : []).slice().sort();
        return a.length === b.length && a.every((x, i) => x === b[i]);
    }
    return value === d;
}

// Build the { plugin: { field: value } } config to POST from a flat values map keyed "pluginId/fieldId".
// Only values that DIFFER from the default are included, keeping the body minimal and letting a later
// default change still reach anyone who left the field alone.
export function collectPluginConfig(configurable, values) {
    const out = {};
    for (const p of configurable) {
        for (const f of p.fields) {
            const key = `${p.id}/${f.id}`;
            if (!(key in values)) continue;
            const v = values[key];
            if (isDefault(f, v)) continue;
            (out[p.id] || (out[p.id] = {}))[f.id] = v;
        }
    }
    return out;
}
